import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAccess, pathIsArea } from "@/lib/access";
import {
  House,
  Users,
  Package,
  SignOut,
  ChartLineUp,
  Kanban,
  Storefront,
  Receipt,
  Cube,
  Gear,
  Bell,
  ShieldCheck,
  Calculator,
  Factory,
  Truck,
  ClipboardText,
} from "@phosphor-icons/react";

const SALES_NAV = [
  { to: "/sales/dashboard", label: "Dashboard", icon: House, testId: "nav-dashboard" },
  { to: "/sales/customers", label: "Customers", icon: Users, testId: "nav-customers" },
  { to: "/sales/pipeline", label: "Pipeline", icon: Kanban, testId: "nav-pipeline" },
  { to: "/sales/orders", label: "Orders", icon: Package, testId: "nav-orders" },
  { to: "/sales/vendors", label: "Vendors", icon: Storefront, testId: "nav-vendors" },
  { to: "/sales/pricing", label: "Pricing", icon: Calculator, testId: "nav-pricing" },
];

const INVOICE_NAV = [
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices", end: true },
  { to: "/invoices/follow-ups", label: "AR Follow-ups", icon: Bell, testId: "nav-ar" },
  { to: "/products", label: "Products", icon: Cube, testId: "nav-products" },
  { to: "/settings", label: "Settings", icon: Gear, testId: "nav-settings" },
];

const INVENTORY_NAV = [
  { to: "/inventory", label: "Dashboard", icon: House, testId: "nav-inv-dashboard", end: true },
  { to: "/inventory/items", label: "Item Master", icon: Cube, testId: "nav-inv-items" },
  { to: "/inventory/stock", label: "Stock Ledger", icon: Package, testId: "nav-inv-stock" },
  { to: "/inventory/recipes", label: "Recipes", icon: Kanban, testId: "nav-inv-recipes" },
  { to: "/inventory/batch-calculator", label: "Batch Calculator", icon: Calculator, testId: "nav-inv-calc" },
  { to: "/inventory/requisitions", label: "Requisitions", icon: ClipboardText, testId: "nav-inv-requisitions" },
  { to: "/inventory/production-runs", label: "Production Runs", icon: Factory, testId: "nav-inv-runs" },
  { to: "/inventory/procurement", label: "Procurement & QC", icon: Truck, testId: "nav-inv-procurement" },
  { to: "/inventory/iso", label: "ISO 22000 / CAPA", icon: ShieldCheck, testId: "nav-inv-iso" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { roles } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = roles.includes("admin");

  const inInvoice = pathIsArea(location.pathname, "invoice");
  const inSales = pathIsArea(location.pathname, "sales");
  const inInventory = pathIsArea(location.pathname, "inventory");
  const currentArea = inInventory ? "inventory" : inInvoice ? "invoice" : inSales ? "sales" : "sales";
  const items = currentArea === "invoice" ? INVOICE_NAV : currentArea === "inventory" ? INVENTORY_NAV : SALES_NAV;

  const accentClass =
    currentArea === "invoice"
      ? "bg-rose-600"
      : currentArea === "inventory"
      ? "bg-emerald-600"
      : "bg-blue-600";
  const subtitle =
    currentArea === "invoice"
      ? "Finance Workspace"
      : currentArea === "inventory"
      ? "Inventory & ERP"
      : "Sales Workspace";
  const BrandIcon =
    currentArea === "invoice" ? Receipt : currentArea === "inventory" ? Package : ChartLineUp;

  return (
    <aside
      className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col min-h-screen"
      data-testid="sidebar"
    >
      <div className="px-6 py-6 border-b border-slate-200">
        <button
          onClick={() => navigate(currentArea === "invoice" ? "/invoices" : currentArea === "inventory" ? "/inventory" : "/sales/dashboard")}
          className="flex items-center gap-2.5 group"
          data-testid="brand-logo-button"
        >
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accentClass}`}>
            <BrandIcon size={20} weight="bold" className="text-white" />
          </div>
          <div className="text-left">
            <div className="font-heading text-lg font-bold tracking-tight text-slate-900 leading-none">
              SVRAF
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">
              {subtitle}
            </div>
          </div>
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testId, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
              "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition " +
              (isActive
                ? currentArea === "invoice"
                  ? "bg-rose-50 text-rose-700"
                  : currentArea === "inventory"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-blue-50 text-blue-700"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900")
            }
          >
            <Icon size={18} weight="duotone" />
            {label}
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="mt-5 mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
              Admin
            </div>
            <NavLink
              to="/admin/roles"
              data-testid="nav-admin-roles"
              className={({ isActive }) =>
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition " +
                (isActive
                  ? "bg-amber-50 text-amber-700"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900")
              }
            >
              <ShieldCheck size={18} weight="duotone" />
              User Roles
            </NavLink>
          </>
        )}
      </nav>

      <div className="px-3 pb-4 border-t border-slate-100 pt-3 space-y-1">
        <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
          {user?.picture && (
            <img
              src={user.picture}
              alt=""
              className="w-7 h-7 rounded-full border border-slate-200 shrink-0"
              data-testid="user-avatar"
            />
          )}
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-900 truncate" data-testid="user-name">
              {user?.name}
            </div>
            <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition"
        >
          <SignOut size={18} weight="duotone" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
